import { OpenInNew } from '@mui/icons-material'
import { Box, Button, Stack, Typography, type SxProps, type Theme } from '@mui/material'
import { StyledToggle, type ToggleOption } from '../../../implementation/StyledToggle'
import { type Priority } from '../../../../../models/Priority'
import { PriorityOneReviewType } from '../../../../../models/PriorityOneReviewType'
import { UpdatePriorityOneReview } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { useState } from 'react'

interface Props {
  priority: Priority
}
export const EPLIManagementTool = (props: Props): JSX.Element => {
  const [initialReviewCompleted, setInitialReviewCompleted] = useState<string>(props.priority.initialReviewCompleted === true ? 'true' : 'false')
  const [finalReviewCompleted, setFinalReviewCompleted] = useState<string>(props.priority.finalReviewCompleted === true ? 'true' : 'false')

  const styles: Record<string, SxProps<Theme>> = {
    container: {
      width: '100%',
      bgcolor: 'var(--blue-500)',
      borderRadius: '12px',
      overflow: 'hidden'
    },
    box: {
      bgcolor: 'var(--white)',
      p: '20px',
      border: '5px solid var(--blue-500)',
      borderTop: 0,
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px'
    },
    button: {
      width: 'fit-content',
      height: 'fit-content',
      padding: '9.5px 20px',
      bgcolor: 'var(--blue-500)',
      color: 'var(--white)',
      textTransform: 'none',
      fontSize: '16px',
      fontWeight: '600',
      gap: '36px',
      mt: 'auto'
    }
  }

  const handleAccessTrainerPortal = (): void => {
    const trainerPortalURL = 'https://www.gvsu.edu/autismcenter/epli-trainer-portal-404.htm'
    window.open(trainerPortalURL, '_blank', 'noopener,noreferrer')
  }

  const toggleOptions: Array<ToggleOption<string>> = [
    { value: 'false', label: 'Incomplete' },
    { value: 'true', label: 'Complete' }
  ]

  const handleReviewChange = async (reviewType: PriorityOneReviewType, value: string): Promise<void> => {
    const isComplete = value === 'true'
    const { success, errors } = await UpdatePriorityOneReview(reviewType, props.priority.id, isComplete)
    if (!success) {
      for (const error of errors) {
        toast.error(error)
      }
      return
    }

    if (reviewType === PriorityOneReviewType.Initial) {
      setInitialReviewCompleted(value)
    } else {
      setFinalReviewCompleted(value)
    }
    toast.success(`${reviewType} Review Updated`)
  }

  return <Stack data-testid='epliManagementTool' sx={styles.container}>
    <Box sx={{ padding: '16px' }}>
      <Typography variant='h3' color='var(--white)'>Current Trainer Review</Typography>
    </Box>
    <Stack gap='20px' sx={styles.box}>
      <Typography variant='body1' fontWeight='600'>
        Please review the EPLI Trainer Portal linked below to confirm that it is up-to-date.
        Complete this review at both the beginning of the year and end of the year.
        Once finished, mark the review as complete here.
      </Typography>

      <Stack direction='row' gap='20px'>
        <Button sx={styles.button} variant='contained' onClick={handleAccessTrainerPortal}>
          Access Trainer Portal
          <OpenInNew color='inherit' />
        </Button>

        <Box>
          <Typography variant='h5'>
            Initial Review Due:
            <span style={{ fontWeight: 'normal', marginLeft: '4px' }}>
              {props.priority.initialReviewDueDate != null ? new Date(props.priority.initialReviewDueDate).toLocaleDateString() : '-'}
            </span>
          </Typography>
          <StyledToggle
            options={toggleOptions}
            selectedValue={initialReviewCompleted}
            onSelectionChange={(value) => { void handleReviewChange(PriorityOneReviewType.Initial, value) }}
            backgroundColor='var(--blue-500)'
            dataTestId='initialReviewToggle'
          />
        </Box>

        <Box>
          <Typography variant='h5'>
            Final Review Due:
            <span style={{ fontWeight: 'normal', marginLeft: '4px' }}>
              {props.priority.finalReviewDueDate != null ? new Date(props.priority.finalReviewDueDate).toLocaleDateString() : '-'}
            </span>
          </Typography>
          <StyledToggle
            options={toggleOptions}
            selectedValue={finalReviewCompleted}
            onSelectionChange={(value) => { void handleReviewChange(PriorityOneReviewType.Final, value) }}
            backgroundColor='var(--blue-500)'
            dataTestId='finalReviewToggle'
          />
        </Box>
      </Stack>
    </Stack>
  </Stack>
}
